<template>
  <nav aria-label="Paging" v-if="hasPages()">
    <ul class="pagination">
      <li class="page-item" v-for="(p, i) in pages()" :key="`${i}`" :class="{ active: p.isActive }">
        <a
          class="page-link"
          :disabled="p.disabled"
          @click="setPageTo(p.number, $event)"
          :href="p.url"
        >
          {{ p.label }}
        </a>
      </li>
    </ul>
  </nav>
</template>

<script>
export default {
  name: 'CIS-Resource-PagedListing-Pagination',
  props: {
    pageInfo: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['setPage'],
  methods: {
    setPageTo(page, event) {
      event.preventDefault();
      this.$emit('setPage', page);
    },
    hasPages() {
      return !!(this.pageInfo?.pages > 1);
    },
    pages() {
      // Inline function to generate URL with new page number, but persisting
      // other values in the URL search string.
      const getUrl = (page) => {
        if (isNaN(page) || page < 1) return '';
        const searchParams = new URLSearchParams(
          typeof window == 'object' && window.location
            ? window.location.search
            : undefined || this.pageInfo.initSearchString
        );
        searchParams.set('page', page);
        searchParams.sort();
        return `?${searchParams.toString()}`;
      };

      const prevPageNumber = this.pageInfo.page - 1;
      const prevPageDisabled = prevPageNumber < 1;
      const output = [
        {
          label: '‹ prev',
          number: prevPageNumber,
          disabled: prevPageDisabled,
          isActive: false,
          url: prevPageDisabled ? '' : getUrl(prevPageNumber)
        }
      ];

      // Create range of pages adjencent to the current page
      const delta = 1, // Size of range before/after current page
        left = this.pageInfo.page - delta,
        right = this.pageInfo.page + delta + 1;

      const pagingRange = Array.from({ length: this.pageInfo.pages }, (v, k) => k + 1).filter(
        (i) => i && i >= left && i < right
      );

      if (pagingRange.length > 1) {
        // Add first page and dots if needed
        if (pagingRange[0] > 1) {
          if (pagingRange[0] > 2) {
            pagingRange.unshift('...');
          }
          pagingRange.unshift(1);
        }
        // Add dots and last page and dots if needed
        if (pagingRange[pagingRange.length - 1] < this.pageInfo.pages) {
          if (pagingRange[pagingRange.length - 1] !== this.pageInfo.pages - 1) {
            pagingRange.push('...');
          }
          pagingRange.push(this.pageInfo.pages);
        }
      }

      // Append range of pages, transformed into paging objects, into array for UI
      output.push(
        ...pagingRange.map((p) => {
          return {
            label: p.toString(),
            number: p,
            disabled: isNaN(p),
            isActive: this.pageInfo.page === p,
            url: getUrl(p)
          };
        })
      );

      const nextPageNumber = this.pageInfo.page + 1;
      const nextPageDisabled = nextPageNumber > this.pageInfo.pages;
      output.push({
        label: 'next ›',
        number: nextPageNumber,
        disabled: nextPageDisabled,
        isActive: false,
        url: nextPageDisabled ? '' : getUrl(nextPageNumber)
      });

      return output;
    }
  }
};
</script>

<style lang="scss" scoped>
nav {
  display: none;
  text-align: center;
  padding-bottom: 2rem;

  @include desktop-up {
    display: block;
  }

  &.c-page-mobile {
    display: block;
    padding-top: 2rem;

    @include desktop-up {
      display: none;
    }
  }

  .pagination {
    margin: 0;

    .page-item {
      margin: 0;

      &:before {
        display: none;
      }

      .page-link {
        border: 0;
        border-radius: 0;
        @include font-styles('Arial', 2rem, 400, normal, 2.4rem, 0, $blue);
        cursor: pointer;
        padding: 0.5rem 0.5rem;
        margin: 0 0.5rem;
        background-color: transparent;

        &:hover {
          background: none;
        }

        &:focus {
          outline: none;
        }

        &[disabled='true'] {
          pointer-events: none;
          color: $gray;
        }

        &.hide {
          display: none;
        }
      }

      &.active {
        .page-link {
          color: $dark-gray;
          background-color: transparent;
          border-bottom: 0.3rem solid $blue;
        }
      }
    }

    :first-child {
      .page-link {
        margin-right: 2rem;
      }
    }

    :last-child {
      .page-link {
        margin-left: 2rem;
      }
    }
  }
}
</style>
